import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Brain, Mic, MessageSquare, Check, ChevronRight, ChevronDown, LogOut, Sun, Moon, Monitor, Phone, ShieldCheck, User, Volume2, PhoneCall, ReceiptText } from 'lucide-react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useProfile } from '../hooks/useProfile';
import { isControlCentreBuildEnabled } from '../featureFlags/controlCentre';
import { callCheckPhoneVerification, callStartPhoneVerification } from '../services/functions';
import BillingDashboard from '../components/BillingDashboard';
import type {
  VoiceProvider,
  ScheduledCallCadence,
  ScheduledCallType,
  VoiceGenderPreference,
  VoicePausePreset,
  VoicePauseTimingPreferences,
} from '../types';

type SettingsTab = 'general' | 'voice' | 'phone' | 'insights' | 'billing';

const BILLING_ADMIN_UID = 'yk4jaCmxp8T9bI9mPPsZMGuAMhE2';

const BASE_TABS: Array<{ id: Exclude<SettingsTab, 'billing'>; label: string; icon: typeof User }> = [
  { id: 'general', label: 'General', icon: User },
  { id: 'voice', label: 'Voice', icon: Volume2 },
  { id: 'phone', label: 'Phone', icon: PhoneCall },
  { id: 'insights', label: 'Insights', icon: ShieldCheck },
];

const BILLING_TAB: { id: SettingsTab; label: string; icon: typeof User } = {
  id: 'billing',
  label: 'Billing',
  icon: ReceiptText,
};

const PROVIDER_OPTIONS: Array<{
  id: VoiceProvider;
  label: string;
  description: string;
  enabled: boolean;
}> = [
  {
    id: 'openai',
    label: 'OpenAI Realtime',
    description: 'Low-latency speech-to-speech via Realtime API',
    enabled: true,
  },
  {
    id: 'gemini',
    label: 'Google Gemini Live',
    description: 'Native live audio via Gemini streaming transport',
    enabled: true,
  },
  {
    id: 'elevenlabs',
    label: 'ElevenLabs',
    description: 'ElevenLabs TTS voice with realtime conversational transport',
    enabled: true,
  },
  {
    id: 'grok',
    label: 'Grok (xAI)',
    description: 'Alternative voice model adapter (coming soon in this app)',
    enabled: false,
  },
];

const VOICE_MODEL_LABEL: Record<VoiceProvider, string> = {
  openai: 'gpt-realtime-2',
  gemini: 'models/gemini-2.5-flash-native-audio-preview-12-2025',
  elevenlabs: 'gpt-realtime-2 (ElevenLabs voice transport)',
  grok: 'grok-realtime',
};

const ENABLED_VOICE_PROVIDERS: VoiceProvider[] = ['openai', 'gemini', 'elevenlabs'];
// Insights is now available to all users — no ID gating.

interface VoiceOption {
  id: string;
  label: string;
  description: string;
  gender: Exclude<VoiceGenderPreference, 'any'> | 'unknown';
  genderSource: 'official' | 'estimated' | 'not_provided';
}

interface PhoneCountryOption {
  iso2: CountryCode;
  dialCode: string;
  label: string;
  flag: string;
}

const VOICE_OPTIONS: Record<VoiceProvider, VoiceOption[]> = {
  openai: [
    { id: 'alloy', label: 'Alloy', description: 'Neutral and balanced', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'ash', label: 'Ash', description: 'Warm and conversational', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'ballad', label: 'Ballad', description: 'Soft and expressive', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'coral', label: 'Coral', description: 'Clear and approachable', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'echo', label: 'Echo', description: 'Smooth and steady', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'sage', label: 'Sage', description: 'Calm and authoritative', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'shimmer', label: 'Shimmer', description: 'Bright and energetic', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'verse', label: 'Verse', description: 'Rich and dynamic', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'marin', label: 'Marin', description: 'Natural and expressive', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'cedar', label: 'Cedar', description: 'Warm and natural', gender: 'unknown', genderSource: 'not_provided' },
  ],
  gemini: [
    { id: 'Zephyr', label: 'Zephyr', description: 'Bright', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Puck', label: 'Puck', description: 'Upbeat', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Charon', label: 'Charon', description: 'Informative', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Kore', label: 'Kore', description: 'Firm', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Fenrir', label: 'Fenrir', description: 'Excitable', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Leda', label: 'Leda', description: 'Youthful', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Orus', label: 'Orus', description: 'Firm', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Aoede', label: 'Aoede', description: 'Breezy', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Callirrhoe', label: 'Callirrhoe', description: 'Easy-going', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Autonoe', label: 'Autonoe', description: 'Bright', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Enceladus', label: 'Enceladus', description: 'Breathy', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Iapetus', label: 'Iapetus', description: 'Clear', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Umbriel', label: 'Umbriel', description: 'Easy-going', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Algieba', label: 'Algieba', description: 'Smooth', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Despina', label: 'Despina', description: 'Smooth', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Erinome', label: 'Erinome', description: 'Clear', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Algenib', label: 'Algenib', description: 'Gravelly', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Rasalgethi', label: 'Rasalgethi', description: 'Informative', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Laomedeia', label: 'Laomedeia', description: 'Upbeat', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Achernar', label: 'Achernar', description: 'Soft', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Alnilam', label: 'Alnilam', description: 'Firm', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Schedar', label: 'Schedar', description: 'Even', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Gacrux', label: 'Gacrux', description: 'Mature', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Pulcherrima', label: 'Pulcherrima', description: 'Forward', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Achird', label: 'Achird', description: 'Friendly', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Zubenelgenubi', label: 'Zubenelgenubi', description: 'Casual', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Vindemiatrix', label: 'Vindemiatrix', description: 'Gentle', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Sadachbia', label: 'Sadachbia', description: 'Lively', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Sadaltager', label: 'Sadaltager', description: 'Knowledgeable', gender: 'unknown', genderSource: 'not_provided' },
    { id: 'Sulafat', label: 'Sulafat', description: 'Warm', gender: 'unknown', genderSource: 'not_provided' },
  ],
  elevenlabs: [
    { id: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel', description: 'Calm and composed', gender: 'female', genderSource: 'estimated' },
    { id: 'AZnzlk1XvdvUeBnXmlld', label: 'Domi', description: 'Strong and confident', gender: 'female', genderSource: 'estimated' },
    { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah', description: 'Soft and gentle', gender: 'female', genderSource: 'estimated' },
    { id: 'ErXwobaYiN019PkySvjV', label: 'Antoni', description: 'Well-rounded and warm', gender: 'male', genderSource: 'estimated' },
    { id: 'MF3mGyEYCl7XYWbV9V6O', label: 'Elli', description: 'Youthful and friendly', gender: 'female', genderSource: 'estimated' },
    { id: 'TxGEqnHWrfWFTfGW9XjX', label: 'Josh', description: 'Deep and authoritative', gender: 'male', genderSource: 'estimated' },
    { id: 'VR6AewLTigWG4xSOukaG', label: 'Arnold', description: 'Crisp and direct', gender: 'male', genderSource: 'estimated' },
    { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam', description: 'Deep and narrating', gender: 'male', genderSource: 'estimated' },
    { id: 'yoZ06aMxZJJ28mfd3POQ', label: 'Sam', description: 'Raspy and authentic', gender: 'male', genderSource: 'estimated' },
  ],
  grok: [
    { id: 'Ara', label: 'Ara', description: 'Warm and friendly', gender: 'female', genderSource: 'official' },
    { id: 'Rex', label: 'Rex', description: 'Confident and clear', gender: 'male', genderSource: 'official' },
    { id: 'Sal', label: 'Sal', description: 'Smooth and balanced', gender: 'neutral', genderSource: 'official' },
    { id: 'Eve', label: 'Eve', description: 'Energetic and upbeat', gender: 'female', genderSource: 'official' },
    { id: 'Leo', label: 'Leo', description: 'Authoritative and strong', gender: 'male', genderSource: 'official' },
  ],
};

const DEFAULT_VOICE: Record<VoiceProvider, string> = {
  openai: 'alloy',
  gemini: 'Kore',
  elevenlabs: '21m00Tcm4TlvDq8ikWAM',
  grok: 'Ara',
};

const VOICE_GENDER_OPTIONS: Array<{ id: VoiceGenderPreference; label: string }> = [
  { id: 'any', label: 'Any' },
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
  { id: 'neutral', label: 'Neutral' },
];

const VOICE_GENDER_LABEL: Record<VoiceOption['gender'], string> = {
  female: 'Female',
  male: 'Male',
  neutral: 'Neutral',
  unknown: 'Not specified',
};

const PAUSE_PRESET_OPTIONS: Array<{ id: VoicePausePreset; label: string }> = [
  { id: 'very_short', label: 'Very short' },
  { id: 'short', label: 'Short' },
  { id: 'balanced', label: 'Balanced' },
  { id: 'long', label: 'Long' },
  { id: 'very_long', label: 'Very long' },
];

const DEFAULT_VOICE_PAUSE_TIMING: VoicePauseTimingPreferences = {
  checkIns: 'short',
  taskPlanning: 'very_short',
  coaching: 'long',
};

const PAUSE_TIMING_GROUPS: Array<{
  id: keyof VoicePauseTimingPreferences;
  label: string;
  description: string;
}> = [
  {
    id: 'checkIns',
    label: 'Check-ins',
    description: 'Morning and evening check-ins',
  },
  {
    id: 'taskPlanning',
    label: 'Task planning',
    description: 'Task planning sessions',
  },
  {
    id: 'coaching',
    label: 'Coaching',
    description: 'Coaching + get-to-know-you sessions',
  },
];

function filterVoiceOptions(options: VoiceOption[], preference: VoiceGenderPreference): VoiceOption[] {
  if (preference === 'any') return options;
  return options.filter(option => option.gender === preference);
}

function resolveVoiceForPreference(provider: VoiceProvider, preference: VoiceGenderPreference): string {
  const options = VOICE_OPTIONS[provider] ?? [];
  const filtered = filterVoiceOptions(options, preference);
  return filtered[0]?.id ?? DEFAULT_VOICE[provider];
}

function resolveSelectableProvider(provider: VoiceProvider | undefined): VoiceProvider {
  if (!provider) return 'openai';
  return ENABLED_VOICE_PROVIDERS.includes(provider) ? provider : 'openai';
}

function resolvePauseTimingPreferences(
  value: VoicePauseTimingPreferences | undefined
): VoicePauseTimingPreferences {
  return {
    checkIns: value?.checkIns ?? DEFAULT_VOICE_PAUSE_TIMING.checkIns,
    taskPlanning: value?.taskPlanning ?? DEFAULT_VOICE_PAUSE_TIMING.taskPlanning,
    coaching: value?.coaching ?? DEFAULT_VOICE_PAUSE_TIMING.coaching,
  };
}

const LANGUAGE_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese' },
];

const SCHEDULED_CALL_CADENCE_OPTIONS: Array<{ id: ScheduledCallCadence; label: string; description: string }> = [
  {
    id: 'weekdays',
    label: 'Weekdays only',
    description: 'Calls run Monday to Friday.',
  },
  {
    id: 'everyday',
    label: 'Every day',
    description: 'Calls run seven days a week.',
  },
];
const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

function toFlagEmoji(countryCode: string): string {
  if (!/^[A-Z]{2}$/.test(countryCode)) return countryCode;
  return String.fromCodePoint(
    countryCode.charCodeAt(0) + 127397,
    countryCode.charCodeAt(1) + 127397
  );
}

const regionNameFormatter = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = getCountries()
  .map(iso2 => ({
    iso2,
    dialCode: `+${getCountryCallingCode(iso2)}`,
    label: regionNameFormatter?.of(iso2) ?? iso2,
    flag: toFlagEmoji(iso2),
  }))
  .sort((a, b) => a.label.localeCompare(b.label, 'en'));

const PHONE_COUNTRY_SET = new Set<CountryCode>(PHONE_COUNTRY_OPTIONS.map(option => option.iso2));

function countryFromLocale(locale: string): CountryCode | null {
  const match = locale.match(/[-_]([A-Za-z]{2})\b/);
  if (!match) return null;
  const region = match[1].toUpperCase() as CountryCode;
  return PHONE_COUNTRY_SET.has(region) ? region : null;
}

function detectBrowserCountry(): CountryCode | null {
  if (typeof navigator === 'undefined') return null;
  const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const locale of locales) {
    const country = countryFromLocale(locale);
    if (country) return country;
  }
  return null;
}

function normalizeToE164PhoneNumber(
  value: string,
  country: CountryCode
): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const compact = trimmed.replace(/[^\d+]/g, '');
  const internationalCandidate = compact.startsWith('00')
    ? `+${compact.slice(2)}`
    : compact;

  const parsed = internationalCandidate.startsWith('+')
    ? parsePhoneNumberFromString(internationalCandidate)
    : parsePhoneNumberFromString(internationalCandidate, country);

  if (!parsed || !parsed.isValid()) return null;
  return parsed.number;
}

function normalizeTimeDraft(value: string): string | null {
  const trimmed = value.trim();
  if (TIME_REGEX.test(trimmed)) return trimmed;

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 4) {
    const hours = Number(digits.slice(0, 2));
    const minutes = Number(digits.slice(2, 4));
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    }
  }

  if (digits.length === 3) {
    const hours = Number(digits.slice(0, 1));
    const minutes = Number(digits.slice(1, 3));
    if (hours >= 0 && hours <= 9 && minutes >= 0 && minutes <= 59) {
      return `0${digits.slice(0, 1)}:${digits.slice(1, 3)}`;
    }
  }

  return null;
}

export default function SettingsPage() {
  const { user, logOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, setTheme } = useTheme();
  const { profile, updatePrefs, updateProfileFields, updateFeatures } = useProfile(user?.uid ?? null);
  const canViewBilling = user?.uid === BILLING_ADMIN_UID;

  const fullName = profile?.displayName ?? user?.displayName ?? '';
  const currentPreferredName = profile?.preferredName ?? fullName.split(' ')[0] ?? '';
  const currentProvider = resolveSelectableProvider(profile?.preferences?.voiceProvider);
  const currentVoiceName = profile?.preferences?.voiceName ?? DEFAULT_VOICE[currentProvider];
  const currentVoiceGenderPreference = profile?.preferences?.voiceGenderPreference ?? 'any';
  const currentVoicePauseTiming = resolvePauseTimingPreferences(profile?.preferences?.voicePauseTiming);
  const currentLanguage = profile?.preferences?.preferredLanguage ?? 'en';
  const currentModality = profile?.preferences?.preferredModality ?? 'voice';
  const currentCountry = profile?.preferences?.countryOfResidence ?? '';
  const currentNationality = profile?.preferences?.nationality ?? '';
  const currentPhoneNumber = profile?.preferences?.phoneNumber ?? '';
  const currentPhoneVerified = Boolean(profile?.preferences?.phoneNumberVerified);
  const currentPhoneCallsEnabled = Boolean(profile?.preferences?.phoneCallsEnabled);
  const showMoodEnergyMorning = profile?.preferences?.showMoodEnergyMorning ?? true;
  const showMoodEnergyEvening = profile?.preferences?.showMoodEnergyEvening ?? true;
  const askMoodEnergyPhone = profile?.preferences?.askMoodEnergyPhone ?? false;
  const currentScheduledCadence = profile?.preferences?.scheduledCallCadence ?? 'weekdays';
  const currentScheduledCallTypes = profile?.preferences?.scheduledCallTypes ?? ['morning_checkin', 'evening_checkin'];
  const currentControlCentreEnabled = Boolean(profile?.features?.controlCentre);
  const controlCentreBuildEnabled = isControlCentreBuildEnabled();
  const morningCallsEnabled = currentScheduledCallTypes.includes('morning_checkin');
  const eveningCallsEnabled = currentScheduledCallTypes.includes('evening_checkin');
  const morningTime = profile?.preferences?.morningCheckInTime ?? '07:30';
  const eveningTime = profile?.preferences?.eveningCheckInTime ?? '20:30';

  const [preferredName, setPreferredName] = useState(currentPreferredName);
  const [countryOfResidence, setCountryOfResidence] = useState(currentCountry);
  const [nationality, setNationality] = useState(currentNationality);
  const [phoneCountryCode, setPhoneCountryCode] = useState<CountryCode>(() => detectBrowserCountry() ?? 'US');
  const [phoneNationalNumber, setPhoneNationalNumber] = useState('');
  const [phoneCountryPickerOpen, setPhoneCountryPickerOpen] = useState(false);
  const [phoneCountrySearch, setPhoneCountrySearch] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState<'idle' | 'sending' | 'checking'>('idle');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [morningTimeDraft, setMorningTimeDraft] = useState(morningTime);
  const [eveningTimeDraft, setEveningTimeDraft] = useState(eveningTime);
  const [voiceOptionsExpanded, setVoiceOptionsExpanded] = useState(false);

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [saveFlash, setSaveFlash] = useState(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const phoneCountryPickerRef = useRef<HTMLDivElement | null>(null);
  const phoneCountrySearchInputRef = useRef<HTMLInputElement | null>(null);
  const requestedTab = searchParams.get('tab');
  const tabs = useMemo(() => (canViewBilling ? [...BASE_TABS, BILLING_TAB] : BASE_TABS), [canViewBilling]);

  const selectedOption = useMemo(
    () => PROVIDER_OPTIONS.find(option => option.id === currentProvider),
    [currentProvider]
  );
  const providerVoiceOptions = useMemo(
    () => VOICE_OPTIONS[currentProvider] ?? [],
    [currentProvider]
  );
  const filteredVoiceOptions = useMemo(
    () => filterVoiceOptions(providerVoiceOptions, currentVoiceGenderPreference),
    [providerVoiceOptions, currentVoiceGenderPreference]
  );
  const detectedPhoneCountry = useMemo(() => detectBrowserCountry() ?? 'US', []);
  const selectedPhoneCountry = useMemo(
    () => PHONE_COUNTRY_OPTIONS.find(option => option.iso2 === phoneCountryCode) ?? PHONE_COUNTRY_OPTIONS[0],
    [phoneCountryCode]
  );
  const filteredPhoneCountryOptions = useMemo(() => {
    const query = phoneCountrySearch.trim().toLowerCase();
    if (!query) return PHONE_COUNTRY_OPTIONS;

    const queryDigits = query.replace(/[^\d+]/g, '');
    return PHONE_COUNTRY_OPTIONS.filter(option => {
      const countryMatch = option.label.toLowerCase().includes(query);
      const dialCodeMatch = queryDigits.length > 0
        ? option.dialCode.includes(queryDigits) || option.dialCode.replace('+', '').includes(queryDigits.replace('+', ''))
        : false;
      return countryMatch || dialCodeMatch;
    });
  }, [phoneCountrySearch]);
  const normalizedPhoneNumber = useMemo(
    () => normalizeToE164PhoneNumber(phoneNationalNumber, phoneCountryCode),
    [phoneNationalNumber, phoneCountryCode]
  );
  const isPhoneNumberValid = useMemo(
    () => Boolean(normalizedPhoneNumber),
    [normalizedPhoneNumber]
  );

  useEffect(() => {
    setPreferredName(currentPreferredName);
    setCountryOfResidence(currentCountry);
    setNationality(currentNationality);
    const parsedPhoneNumber = parsePhoneNumberFromString(currentPhoneNumber);
    if (parsedPhoneNumber) {
      setPhoneCountryCode(parsedPhoneNumber.country ?? detectedPhoneCountry);
      setPhoneNationalNumber(parsedPhoneNumber.formatNational());
    } else {
      setPhoneCountryCode(detectedPhoneCountry);
      setPhoneNationalNumber(currentPhoneNumber);
    }
    setMorningTimeDraft(morningTime);
    setEveningTimeDraft(eveningTime);
    if (currentPhoneVerified) {
      setVerificationSent(false);
      setVerificationCode('');
    }
  }, [
    currentPreferredName,
    currentCountry,
    currentNationality,
    currentPhoneNumber,
    detectedPhoneCountry,
    currentPhoneVerified,
    morningTime,
    eveningTime,
  ]);

  useEffect(() => () => {
    clearTimeout(flashTimerRef.current);
    clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    if (!phoneCountryPickerOpen) return;

    phoneCountrySearchInputRef.current?.focus();

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!phoneCountryPickerRef.current?.contains(event.target as Node)) {
        setPhoneCountryPickerOpen(false);
      }
    };
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPhoneCountryPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [phoneCountryPickerOpen]);

  useEffect(() => {
    if (requestedTab === 'general' || requestedTab === 'voice' || requestedTab === 'phone' || requestedTab === 'insights') {
      setActiveTab(requestedTab);
      return;
    }

    if (canViewBilling && requestedTab === 'billing') {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, canViewBilling]);

  useEffect(() => {
    if (activeTab === 'billing' && !canViewBilling) {
      setActiveTab('general');
    }
  }, [activeTab, canViewBilling]);

  const flash = useCallback(() => {
    setSaveFlash(true);
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setSaveFlash(false), 1500);
  }, []);

  const savePref = useCallback(
    (prefs: Parameters<typeof updatePrefs>[0]) => {
      updatePrefs(prefs).then(flash).catch(() => {});
    },
    [updatePrefs, flash]
  );

  const saveTextField = useCallback(
    (field: 'preferredName' | 'countryOfResidence' | 'nationality', value: string) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (field === 'preferredName') {
          const trimmed = value.trim() || fullName.split(' ')[0];
          if (trimmed !== currentPreferredName) {
            updateProfileFields({ preferredName: trimmed }).then(flash).catch(() => {});
          }
        } else {
          const trimmed = value.trim() || undefined;
          const current = field === 'countryOfResidence' ? currentCountry : currentNationality;
          if ((trimmed ?? '') !== current) {
            savePref({ [field]: trimmed });
          }
        }
      }, 600);
    },
    [fullName, currentPreferredName, currentCountry, currentNationality, updateProfileFields, savePref, flash]
  );

  const handleVoiceProviderChange = (provider: VoiceProvider) => {
    savePref({
      voiceProvider: provider,
      voiceName: resolveVoiceForPreference(provider, currentVoiceGenderPreference),
    });
  };

  const handleVoiceGenderPreferenceChange = (preference: VoiceGenderPreference) => {
    const preferredOptions = filterVoiceOptions(providerVoiceOptions, preference);
    const nextVoiceName = preferredOptions.some(option => option.id === currentVoiceName)
      ? currentVoiceName
      : (preferredOptions[0]?.id ?? DEFAULT_VOICE[currentProvider]);

    savePref({
      voiceGenderPreference: preference,
      voiceName: nextVoiceName,
    });
  };

  const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'object' && error !== null && 'message' in error) {
      const value = (error as { message?: unknown }).message;
      if (typeof value === 'string' && value.length > 0) return value;
    }
    return 'Something went wrong. Please try again.';
  };

  const handleSendPhoneCode = async () => {
    if (!isPhoneNumberValid) {
      setPhoneError('Enter a valid mobile number for the selected country code.');
      return;
    }

    setPhoneError(null);
    setPhoneBusy('sending');
    try {
      const result = await callStartPhoneVerification(normalizedPhoneNumber!);
      const parsedResult = parsePhoneNumberFromString(result.phoneNumber);
      if (parsedResult) {
        setPhoneCountryCode(parsedResult.country ?? phoneCountryCode);
        setPhoneNationalNumber(parsedResult.formatNational());
      } else {
        setPhoneNationalNumber(result.phoneNumber);
      }
      setVerificationSent(true);
      setVerificationCode('');
      flash();
    } catch (error) {
      setPhoneError(getErrorMessage(error));
    } finally {
      setPhoneBusy('idle');
    }
  };

  const handleConfirmPhoneCode = async () => {
    if (!isPhoneNumberValid) {
      setPhoneError('Enter a valid mobile number for the selected country code.');
      return;
    }

    setPhoneError(null);
    setPhoneBusy('checking');
    try {
      const result = await callCheckPhoneVerification(normalizedPhoneNumber!, verificationCode);
      if (result.approved) {
        setVerificationSent(false);
        setVerificationCode('');
        flash();
        return;
      }
      setPhoneError('That code was not valid. Please try again.');
    } catch (error) {
      setPhoneError(getErrorMessage(error));
    } finally {
      setPhoneBusy('idle');
    }
  };

  const commitMorningTime = () => {
    const normalized = normalizeTimeDraft(morningTimeDraft);
    if (!normalized) {
      setMorningTimeDraft(morningTime);
      return;
    }
    setMorningTimeDraft(normalized);
    if (normalized !== morningTime) {
      savePref({ morningCheckInTime: normalized });
    }
  };

  const commitEveningTime = () => {
    const normalized = normalizeTimeDraft(eveningTimeDraft);
    if (!normalized) {
      setEveningTimeDraft(eveningTime);
      return;
    }
    setEveningTimeDraft(normalized);
    if (normalized !== eveningTime) {
      savePref({ eveningCheckInTime: normalized });
    }
  };

  const handleScheduledTypeToggle = (type: ScheduledCallType, enabled: boolean) => {
    const next = enabled
      ? Array.from(new Set([...currentScheduledCallTypes, type]))
      : currentScheduledCallTypes.filter(currentType => currentType !== type);
    savePref({ scheduledCallTypes: next });
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="px-5 pt-6 pb-4 border-b border-[var(--color-border)] flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Settings</h1>
          <p className="text-xs text-[var(--color-text-muted)]">Changes save automatically</p>
        </div>
        <div
          className="flex items-center gap-1 text-xs font-medium transition-opacity duration-300"
          style={{ color: 'var(--color-success)', opacity: saveFlash ? 1 : 0 }}
        >
          <Check size={14} />
          Saved
        </div>
      </header>

      <nav className="border-b border-[var(--color-border)]">
        <div className={`${activeTab === 'billing' ? 'max-w-7xl' : 'max-w-xl'} mx-auto flex`}>
          {tabs.map(tab => {
            const active = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors relative"
                style={{
                  color: active ? 'var(--color-brand-primary)' : 'var(--color-text-muted)',
                }}
              >
                <Icon size={15} />
                <span className="hidden sm:inline">{tab.label}</span>
                {active && (
                  <span
                    className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                    style={{ background: 'var(--color-brand-primary)' }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      <main className={`${activeTab === 'billing' ? 'px-4 sm:px-5 py-5 max-w-7xl' : 'px-5 py-5 max-w-xl'} mx-auto`}>

        {activeTab === 'general' && (
          <>
            <section className="flank-card rounded-2xl p-4 sm:p-5">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Profile</h2>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  What should Flank call you?
                </p>
              </div>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-[var(--color-text-secondary)]">Preferred name</span>
                <input
                  value={preferredName}
                  onChange={e => { setPreferredName(e.target.value); saveTextField('preferredName', e.target.value); }}
                  onBlur={() => saveTextField('preferredName', preferredName)}
                  placeholder={fullName.split(' ')[0] || 'Your name'}
                  className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                  style={{ borderColor: 'var(--color-border)' }}
                />
                <span className="text-[11px] text-[var(--color-text-muted)]">
                  Used in greetings and throughout the app
                </span>
              </label>
            </section>

            <section className="flank-card rounded-2xl p-4 sm:p-5 mt-4">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Default Session Mode</h2>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  Choose whether sessions start with voice or text by default.
                </p>
              </div>

              <div className="flex gap-2">
                {([
                  { id: 'voice' as const, label: 'Voice', icon: <Mic size={16} />, desc: 'Talk to Flank using your mic' },
                  { id: 'text' as const, label: 'Text', icon: <MessageSquare size={16} />, desc: 'Type your messages instead' },
                ]).map(option => {
                  const checked = currentModality === option.id;
                  return (
                    <label
                      key={option.id}
                      className="flex-1 rounded-xl border p-3 flex items-start gap-2.5 cursor-pointer transition-colors"
                      style={{
                        borderColor: checked ? 'var(--color-brand-primary)' : 'var(--color-border)',
                        background: checked ? 'var(--color-surface-elevated)' : 'var(--color-surface)',
                      }}
                    >
                      <input
                        type="radio"
                        name="session-mode"
                        value={option.id}
                        checked={checked}
                        onChange={() => savePref({ preferredModality: option.id })}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span style={{ color: checked ? 'var(--color-brand-primary)' : 'var(--color-text-muted)' }}>{option.icon}</span>
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">{option.label}</span>
                        </div>
                        <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">{option.desc}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>

            <section className="flank-card rounded-2xl p-4 sm:p-5 mt-4">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Appearance</h2>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  Choose how Flank looks on your device.
                </p>
              </div>

              <div className="flex gap-2">
                {([
                  { id: 'light' as const, label: 'Light', icon: <Sun size={16} /> },
                  { id: 'dark' as const, label: 'Dark', icon: <Moon size={16} /> },
                  { id: 'system' as const, label: 'System', icon: <Monitor size={16} /> },
                ]).map(option => {
                  const checked = theme === option.id;
                  return (
                    <label
                      key={option.id}
                      className="flex-1 rounded-xl border p-3 flex flex-col items-center gap-2 cursor-pointer transition-colors"
                      style={{
                        borderColor: checked ? 'var(--color-brand-primary)' : 'var(--color-border)',
                        background: checked ? 'var(--color-surface-elevated)' : 'var(--color-surface)',
                      }}
                    >
                      <input
                        type="radio"
                        name="theme"
                        value={option.id}
                        checked={checked}
                        onChange={() => setTheme(option.id)}
                        className="sr-only"
                      />
                      <span style={{ color: checked ? 'var(--color-brand-primary)' : 'var(--color-text-muted)' }}>
                        {option.icon}
                      </span>
                      <span className="text-xs font-medium text-[var(--color-text-primary)]">{option.label}</span>
                    </label>
                  );
                })}
              </div>
            </section>

            <section className="flank-card rounded-2xl p-4 sm:p-5 mt-4">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Mood + energy quick check-in</h2>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  Control where Flank shows the optional dual-slider prompt.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-2.5 rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={showMoodEnergyMorning}
                    onChange={(event) => savePref({ showMoodEnergyMorning: event.target.checked })}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text-primary)]">
                      Show in morning sessions
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      Displays the quick mood/energy card near the top of morning check-ins.
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={showMoodEnergyEvening}
                    onChange={(event) => savePref({ showMoodEnergyEvening: event.target.checked })}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text-primary)]">
                      Show in evening sessions
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      Displays the quick mood/energy card near the top of evening reflections.
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={askMoodEnergyPhone}
                    onChange={(event) => savePref({ askMoodEnergyPhone: event.target.checked })}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text-primary)]">
                      Ask in phone check-ins
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      Enables a lightweight mood/energy ask for telephony flows when available.
                    </p>
                  </div>
                </label>
              </div>
            </section>

            <section className="flank-card rounded-2xl p-4 sm:p-5 mt-4">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Language and Cultural Context</h2>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  Flank uses this to keep coaching and voice transcription in the right language and context.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-[var(--color-text-secondary)]">Preferred language</span>
                  <select
                    value={currentLanguage}
                    onChange={e => savePref({ preferredLanguage: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    {LANGUAGE_OPTIONS.map(option => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                    Country of residence (optional)
                  </span>
                  <input
                    value={countryOfResidence}
                    onChange={e => { setCountryOfResidence(e.target.value); saveTextField('countryOfResidence', e.target.value); }}
                    onBlur={() => saveTextField('countryOfResidence', countryOfResidence)}
                    placeholder="e.g. United Kingdom"
                    className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-[var(--color-text-secondary)]">Nationality (optional)</span>
                  <input
                    value={nationality}
                    onChange={e => { setNationality(e.target.value); saveTextField('nationality', e.target.value); }}
                    onBlur={() => saveTextField('nationality', nationality)}
                    placeholder="e.g. Irish"
                    className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </label>
              </div>
            </section>

            {controlCentreBuildEnabled && (
              <section className="flank-card rounded-2xl p-4 sm:p-5 mt-4">
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Experimental Features</h2>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                    Optional capabilities that are in controlled rollout.
                  </p>
                </div>

                <label className="flex items-start gap-2.5 rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={currentControlCentreEnabled}
                    onChange={event => {
                      updateFeatures({ controlCentre: event.target.checked }).then(flash).catch(() => {});
                    }}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text-primary)]">Enable Control Centre</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      Shows the optional Control Centre module if backend and rollout gates also allow access.
                    </p>
                  </div>
                </label>
              </section>
            )}

            <button
                onClick={() => navigate('/insights')}
                className="flank-card rounded-2xl p-4 sm:p-5 mt-4 w-full flex items-center gap-3 text-left transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'color-mix(in srgb, var(--color-brand-primary) 15%, transparent)' }}
                >
                  <BeliefGraphIcon />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Insights</h2>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                    View and manage what Flank knows about you
                  </p>
                </div>
                <ChevronRight size={18} className="text-[var(--color-text-muted)] shrink-0" />
              </button>

            <div className="mt-8 mb-6">
              <button
                onClick={logOut}
                className="w-full rounded-2xl border p-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors hover:bg-[var(--color-surface)]"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-error)' }}
              >
                <LogOut size={16} />
                Log out
              </button>
            </div>
          </>
        )}

        {activeTab === 'voice' && (
          <>
            <section className="flank-card rounded-2xl p-4 sm:p-5">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Voice Provider</h2>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  Choose which voice backend to use for new voice sessions.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                {PROVIDER_OPTIONS.map(option => {
                  const checked = currentProvider === option.id;
                  return (
                    <label
                      key={option.id}
                      className={`rounded-xl border p-3 flex items-start gap-3 ${
                        option.enabled ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'
                      }`}
                      style={{
                        borderColor: checked ? 'var(--color-brand-primary)' : 'var(--color-border)',
                        background: checked ? 'var(--color-surface-elevated)' : 'var(--color-surface)',
                      }}
                    >
                      <input
                        type="radio"
                        name="voice-provider"
                        value={option.id}
                        checked={checked}
                        disabled={!option.enabled}
                        onChange={() => handleVoiceProviderChange(option.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">{option.label}</span>
                          {!option.enabled && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-surface-elevated)] text-[var(--color-text-muted)]">
                              Coming soon
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{option.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {selectedOption && (
                <p className="text-xs text-[var(--color-text-muted)] mt-3">
                  Active selection: {selectedOption.label}
                </p>
              )}
            </section>

            {providerVoiceOptions.length > 0 && (
              <section className="flank-card rounded-2xl p-4 sm:p-5 mt-4">
                <button
                  type="button"
                  onClick={() => setVoiceOptionsExpanded(prev => !prev)}
                  aria-expanded={voiceOptionsExpanded}
                  aria-controls="voice-options-panel"
                  className="w-full flex items-start gap-3 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Voice</h2>
                    <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                      Choose the voice {selectedOption?.label ?? currentProvider} uses for your sessions.
                    </p>
                  </div>
                  <ChevronDown
                    size={16}
                    className="mt-0.5 shrink-0 text-[var(--color-text-muted)] transition-transform duration-200"
                    style={{ transform: voiceOptionsExpanded ? 'rotate(180deg)' : undefined }}
                  />
                </button>

                <div
                  id="voice-options-panel"
                  className="grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] mt-4"
                  style={{ gridTemplateRows: voiceOptionsExpanded ? '1fr' : '0fr', opacity: voiceOptionsExpanded ? 1 : 0.9 }}
                >
                  <div className="overflow-hidden">
                    <div style={{ pointerEvents: voiceOptionsExpanded ? 'auto' : 'none' }}>
                      <div className="mb-4">
                        <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-2">Voice gender</p>
                        <div className="flex flex-wrap gap-2">
                          {VOICE_GENDER_OPTIONS.map(option => {
                            const checked = currentVoiceGenderPreference === option.id;
                            return (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => handleVoiceGenderPreferenceChange(option.id)}
                                className="rounded-full border px-3 py-1 text-xs font-medium transition-colors"
                                style={{
                                  borderColor: checked ? 'var(--color-brand-primary)' : 'var(--color-border)',
                                  color: checked ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
                                  background: checked ? 'var(--color-surface-elevated)' : 'var(--color-surface)',
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {filteredVoiceOptions.length === 0 ? (
                        <div className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}>
                          No voices match this gender filter for {selectedOption?.label ?? currentProvider}.
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {filteredVoiceOptions.map(voice => {
                            const checked = currentVoiceName === voice.id;
                            return (
                              <label
                                key={voice.id}
                                className="rounded-xl border p-3 flex items-start gap-2.5 cursor-pointer transition-colors"
                                style={{
                                  borderColor: checked ? 'var(--color-brand-primary)' : 'var(--color-border)',
                                  background: checked ? 'var(--color-surface-elevated)' : 'var(--color-surface)',
                                }}
                              >
                                <input
                                  type="radio"
                                  name="voice-name"
                                  value={voice.id}
                                  checked={checked}
                                  onChange={() => savePref({ voiceName: voice.id })}
                                  className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium text-[var(--color-text-primary)]">{voice.label}</span>
                                  <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">{voice.description}</p>
                                  <p className="text-[10px] text-[var(--color-text-muted)] mt-1">
                                    {VOICE_GENDER_LABEL[voice.gender]}
                                    {voice.genderSource === 'official' ? '' : voice.genderSource === 'estimated' ? ' (estimated)' : ''}
                                  </p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      <p className="text-[11px] text-[var(--color-text-muted)] mt-3">
                        Gender metadata varies by provider: Grok labels are official, ElevenLabs labels are estimated from known presets, and OpenAI/Gemini do not publish explicit gender metadata.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="flank-card rounded-2xl p-4 sm:p-5 mt-4">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Model pause timing</h2>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  Control how long Flank waits after you stop speaking before responding.
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-2">
                  Applies to {selectedOption?.label ?? currentProvider} ({VOICE_MODEL_LABEL[currentProvider]}).
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {PAUSE_TIMING_GROUPS.map(group => (
                  <div
                    key={group.id}
                    className="rounded-xl border p-3"
                    style={{ borderColor: 'var(--color-border)', background: 'var(--color-surface)' }}
                  >
                    <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{group.label}</h3>
                    <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{group.description}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
                      {PAUSE_PRESET_OPTIONS.map(option => {
                        const checked = currentVoicePauseTiming[group.id] === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              if (checked) return;
                              savePref({
                                voicePauseTiming: {
                                  ...currentVoicePauseTiming,
                                  [group.id]: option.id,
                                },
                              });
                            }}
                            className="rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
                            style={{
                              borderColor: checked ? 'var(--color-brand-primary)' : 'var(--color-border)',
                              color: checked ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
                              background: checked ? 'var(--color-surface-elevated)' : 'var(--color-surface)',
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-[var(--color-text-muted)] mt-3">
                These controls are model-specific. Switch provider to tune pause timing for another model.
              </p>
            </section>
          </>
        )}

        {activeTab === 'phone' && (
          <section className="flank-card rounded-2xl p-4 sm:p-5">
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Phone Coaching</h2>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                Get daily check-ins as mobile phone calls using your saved schedule.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-[var(--color-text-secondary)]">Mobile number</span>
                <div className="flex gap-2">
                  <div className="relative" ref={phoneCountryPickerRef}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!phoneCountryPickerOpen) setPhoneCountrySearch('');
                        setPhoneCountryPickerOpen(open => !open);
                      }}
                      className="h-full min-w-28 rounded-lg border px-2 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)] inline-flex items-center justify-between gap-2"
                      style={{ borderColor: 'var(--color-border)' }}
                      aria-haspopup="listbox"
                      aria-expanded={phoneCountryPickerOpen}
                    >
                      <span>{selectedPhoneCountry.flag} {selectedPhoneCountry.dialCode}</span>
                      <ChevronDown size={14} className="text-[var(--color-text-muted)]" />
                    </button>
                    {phoneCountryPickerOpen && (
                      <div
                        className="absolute z-30 mt-1 w-72 rounded-lg border shadow-lg p-2"
                        style={{
                          borderColor: 'var(--color-border)',
                          background: 'var(--color-surface-elevated)',
                        }}
                      >
                        <input
                          ref={phoneCountrySearchInputRef}
                          value={phoneCountrySearch}
                          onChange={e => setPhoneCountrySearch(e.target.value)}
                          placeholder="Search country or code"
                          className="w-full rounded-md border px-2 py-1.5 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                          style={{ borderColor: 'var(--color-border)' }}
                        />
                        <div className="mt-2 max-h-64 overflow-y-auto flex flex-col gap-1">
                          {filteredPhoneCountryOptions.length === 0 && (
                            <p className="px-2 py-1 text-xs text-[var(--color-text-muted)]">
                              No matches found.
                            </p>
                          )}
                          {filteredPhoneCountryOptions.map(option => (
                            <button
                              key={option.iso2}
                              type="button"
                              onClick={() => {
                                setPhoneCountryCode(option.iso2);
                                setPhoneCountryPickerOpen(false);
                                setPhoneCountrySearch('');
                                if (phoneError) setPhoneError(null);
                              }}
                              className="w-full rounded-md px-2 py-1.5 text-sm text-left hover:bg-[var(--color-surface)] inline-flex items-center justify-between gap-2"
                            >
                              <span className="truncate">
                                {option.flag} {option.label}
                              </span>
                              <span className="text-[var(--color-text-secondary)]">{option.dialCode}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    value={phoneNationalNumber}
                    onChange={e => {
                      setPhoneNationalNumber(e.target.value);
                      if (phoneError) setPhoneError(null);
                    }}
                    placeholder="Local number"
                    className="flex-1 rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                  <button
                    type="button"
                    onClick={handleSendPhoneCode}
                    disabled={phoneBusy !== 'idle' || !isPhoneNumberValid}
                    className="flank-btn flank-btn-secondary whitespace-nowrap"
                  >
                    {phoneBusy === 'sending' ? 'Sending...' : 'Verify'}
                  </button>
                </div>
                {isPhoneNumberValid && normalizedPhoneNumber && (
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    Will verify as {normalizedPhoneNumber}.
                  </span>
                )}
                {phoneNationalNumber.trim().length > 0 && !isPhoneNumberValid && (
                  <span className="text-[11px] text-[var(--color-error)]">
                    Enter a valid number for {selectedPhoneCountry.label} ({selectedPhoneCountry.dialCode}).
                  </span>
                )}
              </label>

              <div
                className="rounded-lg border px-3 py-2 flex items-center gap-2 text-xs"
                style={{
                  borderColor: currentPhoneVerified ? 'var(--color-success)' : 'var(--color-border)',
                  color: currentPhoneVerified ? 'var(--color-success)' : 'var(--color-text-secondary)',
                  background: 'var(--color-surface)',
                }}
              >
                {currentPhoneVerified ? <ShieldCheck size={14} /> : <Phone size={14} />}
                <span>{currentPhoneVerified ? 'Phone verified' : 'Phone not verified yet'}</span>
              </div>

              {(verificationSent || !currentPhoneVerified) && (
                <div className="rounded-lg border p-3 flex flex-col gap-2" style={{ borderColor: 'var(--color-border)' }}>
                  <span className="text-xs font-medium text-[var(--color-text-secondary)]">SMS verification code</span>
                  <div className="flex gap-2">
                    <input
                      value={verificationCode}
                      onChange={e => setVerificationCode(e.target.value)}
                      placeholder="6-digit code"
                      className="flex-1 rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                      style={{ borderColor: 'var(--color-border)' }}
                    />
                    <button
                      type="button"
                      onClick={handleConfirmPhoneCode}
                      disabled={phoneBusy !== 'idle' || !isPhoneNumberValid || verificationCode.trim().length < 4}
                      className="flank-btn flank-btn-primary whitespace-nowrap"
                    >
                      {phoneBusy === 'checking' ? 'Checking...' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}

              {phoneError && (
                <p className="text-xs" style={{ color: 'var(--color-error)' }}>
                  {phoneError}
                </p>
              )}

              <label className="flex items-start gap-2.5 rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={currentPhoneCallsEnabled}
                  onChange={e => savePref({ phoneCallsEnabled: e.target.checked })}
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">Enable phone calls</p>
                  <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                    Allows scheduled check-ins and on-demand &quot;Call me&quot; sessions.
                  </p>
                </div>
              </label>

              <div className="rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-2">Check-in times</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="inline-flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                      <input
                        type="checkbox"
                        checked={morningCallsEnabled}
                        onChange={e => handleScheduledTypeToggle('morning_checkin', e.target.checked)}
                      />
                      Morning
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={morningTimeDraft}
                      disabled={!morningCallsEnabled}
                      onChange={e => setMorningTimeDraft(e.target.value)}
                      onBlur={commitMorningTime}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitMorningTime();
                        }
                      }}
                      placeholder="07:30"
                      className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                      style={{
                        borderColor: 'var(--color-border)',
                        opacity: morningCallsEnabled ? 1 : 0.55,
                        cursor: morningCallsEnabled ? 'text' : 'not-allowed',
                      }}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="inline-flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                      <input
                        type="checkbox"
                        checked={eveningCallsEnabled}
                        onChange={e => handleScheduledTypeToggle('evening_checkin', e.target.checked)}
                      />
                      Evening
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={eveningTimeDraft}
                      disabled={!eveningCallsEnabled}
                      onChange={e => setEveningTimeDraft(e.target.value)}
                      onBlur={commitEveningTime}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitEveningTime();
                        }
                      }}
                      placeholder="20:30"
                      className="rounded-lg border px-3 py-2 text-sm bg-[var(--color-background)] text-[var(--color-text-primary)]"
                      style={{
                        borderColor: 'var(--color-border)',
                        opacity: eveningCallsEnabled ? 1 : 0.55,
                        cursor: eveningCallsEnabled ? 'text' : 'not-allowed',
                      }}
                    />
                  </label>
                </div>
                <p className="text-[11px] text-[var(--color-text-muted)] mt-2">
                  Use 24-hour time in HH:mm (for example, 07:30 or 19:00).
                </p>
                <p className="text-[11px] text-[var(--color-text-muted)] mt-1">
                  Enable Morning and/or Evening to choose which scheduled call types run.
                </p>
              </div>

              <div className="rounded-lg border p-3 flex flex-col gap-2" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-xs font-medium text-[var(--color-text-secondary)]">Call scheduling</p>
                {SCHEDULED_CALL_CADENCE_OPTIONS.map(option => {
                  const checked = currentScheduledCadence === option.id;
                  return (
                    <label key={option.id} className="flex items-start gap-2.5">
                      <input
                        type="radio"
                        name="scheduled-call-cadence"
                        className="mt-0.5"
                        checked={checked}
                        onChange={() => savePref({ scheduledCallCadence: option.id })}
                      />
                      <div>
                        <p className="text-sm text-[var(--color-text-primary)]">{option.label}</p>
                        <p className="text-xs text-[var(--color-text-muted)]">{option.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'insights' && (
          <section className="flank-card rounded-2xl p-4 sm:p-5">
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Insights</h2>
              <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                Open beliefs and memories separately to review or manage each model.
              </p>
            </div>
            <div className="grid gap-3">
              <button
                onClick={() => navigate('/insights')}
                className="w-full rounded-xl border px-4 py-3 flex items-center gap-3 text-left transition-colors hover:bg-[var(--color-surface)]"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'color-mix(in srgb, var(--color-brand-primary) 15%, transparent)' }}
                >
                  <BeliefGraphIcon />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">Beliefs</p>
                  <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                    Core, active, and situational belief graph with controls
                  </p>
                </div>
                <ChevronRight size={18} className="text-[var(--color-text-muted)] shrink-0" />
              </button>

              <button
                onClick={() => navigate('/insights/memories')}
                className="w-full rounded-xl border px-4 py-3 flex items-center gap-3 text-left transition-colors hover:bg-[var(--color-surface)]"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'color-mix(in srgb, var(--color-brand-primary) 15%, transparent)' }}
                >
                  <Brain size={20} style={{ color: 'var(--color-brand-primary)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">Memories</p>
                  <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                    About you, commitments, events, coaching notes, and suppression
                  </p>
                </div>
                <ChevronRight size={18} className="text-[var(--color-text-muted)] shrink-0" />
              </button>
            </div>
          </section>
        )}

        {activeTab === 'billing' && canViewBilling && (
          <BillingDashboard />
        )}

      </main>
    </div>
  );
}

function BeliefGraphIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      aria-hidden="true"
      style={{ color: 'var(--color-brand-primary)' }}
    >
      <line x1="12" y1="3" x2="20.6" y2="9.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="20.6" y1="9.2" x2="17.3" y2="19.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="17.3" y1="19.3" x2="6.7" y2="19.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="6.7" y1="19.3" x2="3.4" y2="9.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="3.4" y1="9.2" x2="12" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="20.6" y1="9.2" x2="6.7" y2="19.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="3.4" y1="9.2" x2="17.3" y2="19.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="12" cy="3" r="2.5" fill="var(--color-background, white)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="20.6" cy="9.2" r="2.5" fill="var(--color-background, white)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="17.3" cy="19.3" r="2.5" fill="var(--color-background, white)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="6.7" cy="19.3" r="2.5" fill="var(--color-background, white)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="3.4" cy="9.2" r="2.5" fill="var(--color-background, white)" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
